import React from "react";
import { Typography } from "@mui/material";
import { Form, useNavigate } from "react-router-dom";
import { apiClientHooks } from "../bootstrapping/InitApiClient";
import { queryClient } from "../bootstrapping/InitReactQuery";
import { schemas } from "../generated/api/client";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import SingleClickWaitingButton from "../components/SingleClickWaitingButton";
import { TextFieldElement } from "react-hook-form-mui";

const zFormSchema = schemas.EBulkApplicationPayment
type FormSchema = z.infer<typeof zFormSchema>

type ApplicationPaymentExternalFormProps = {
  application_id: string,
  price: number | undefined,
  payment_method: FormSchema['payment_method'],
  payment_reference_text?: React.ReactNode,
  buttonText: string,
}

const ApplicationPaymentExternalForm: React.FC<ApplicationPaymentExternalFormProps> = props => {
  const navigate = useNavigate()

  const payForEBulkApplicationByID = apiClientHooks.usePayForEBulkApplicationByID({
    params: {
      id: props.application_id,
    },
  })

  const onPayByExternal = async (payment_method: 'external_stripe_paymentlink' | 'external_creditcard' | 'external_other', payment_pence: number, payment_reference: string) => {
    if (!props.price) throw new Error('Price is required')
    await payForEBulkApplicationByID.mutateAsync({
      payment_method,
      payment_pence,
      payment_reference,
    })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplicationByID') })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplications') })
    queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getGlobalStats') })
    navigate(`/`)
  }

  // Build the Form
  const form = useForm<FormSchema>({
    resolver: zodResolver(
      zFormSchema
        .superRefine((value, context) => {
          // Ensure the payment_reference starts with "pi_"
          if (value.payment_method === 'external_stripe_paymentlink' && !value.payment_reference.startsWith('pi_')) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Payment Reference must start with "pi_"',
              path: ['payment_reference'],
            })
          }
        })
    ),
    mode: 'onBlur',
    defaultValues: {
      payment_method: props.payment_method,
      payment_pence: props.price,
    },
  })

  // Build the output
  const textFieldElementCommonProps = {
    control: form.control,
    margin: "normal" as const,
    fullWidth: true,
  }

  return <>
    <Form method="post">
      {props.payment_reference_text}

      <TextFieldElement {...textFieldElementCommonProps} label="Payment Reference" name="payment_reference" />

      <Typography component="p" variant="body2" marginTop={2}>
        This is the amount of the total paid by the customer <strong>as a whole number in pennies</strong>, which relates to this application. Defaults to the amount expected for this application, and will usually be this number.
      </Typography>

      <TextFieldElement
        {...textFieldElementCommonProps}
        label="Payment Amount"
        name="payment_pence"
        type="number"
        inputProps={{ min: 0 }}
      />

      <SingleClickWaitingButton
        type="submit"
        fullWidth
        variant="contained"
        color={form.formState.isValid ? 'primary' : 'inherit'}
        onClick={async event => {
          const isValid = await form.trigger()
          if (!isValid) {
            console.warn("Form is not valid", form.formState.errors)
            event.preventDefault()
          } else {
            const values = form.getValues()
            if (
              values.payment_method !== 'external_stripe_paymentlink'
              && values.payment_method !== 'external_creditcard'
              && values.payment_method !== 'external_other') {
              console.warn("Payment method unexpected: ", values.payment_method)
              return
            }

            await onPayByExternal(values.payment_method, values.payment_pence, values.payment_reference)
          }
        }}
        sx={{ mt: 3, mb: 2 }}
      >
        {props.buttonText}
      </SingleClickWaitingButton>
    </Form>
  </>
}

export default ApplicationPaymentExternalForm;
